import { getJwt } from '../auth/session.ts';

export type HttpResponse<T> = { result?: T, error?: string };

async function errorFrom(response: Response): Promise<string> {
  try {
    const body = await response.json();
    if (body && typeof body.error === 'string') {
      return body.error;
    }
  } catch {
    // Non-JSON error body — fall through to the HTTP status text.
  }

  return response.statusText;
}

export async function get<T>(url: string): Promise<HttpResponse<T>> {
  try {
    const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/${url}`, {
      headers: new Headers({
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${getJwt()}`,
      }),
      mode: 'cors',
    });

    if (!response.ok) {
      return { error: response.statusText };
    }

    const resultText = await response.text();
    return { result: resultText ? JSON.parse(resultText) : null };
  } catch (error) {
    return { error: error?.toString() || 'Something went wrong' };
  }
}

export async function post<TBody, TResult>(url: string, body: TBody): Promise<HttpResponse<TResult>> {
  try {
    const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/${url}`, {
      method: 'POST',
      headers: new Headers({
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${getJwt()}`,
      }),
      mode: 'cors',
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      return { error: response.statusText };
    }

    return { result: await response.json() };
  } catch (error) {
    return { error: error?.toString() || 'Something went wrong' };
  }
}

export async function patch<TBody, TResult>(url: string, body: TBody): Promise<HttpResponse<TResult>> {
  try {
    const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/${url}`, {
      method: 'PATCH',
      headers: new Headers({
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${getJwt()}`,
      }),
      mode: 'cors',
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      return { error: response.statusText };
    }

    return { result: await response.json() };
  } catch (error) {
    return { error: error?.toString() || 'Something went wrong' };
  }
}

export async function put<TBody, TResult>(
  url: string,
  body: TBody,
  options?: { keepalive?: boolean }
): Promise<HttpResponse<TResult>> {
  try {
    const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/${url}`, {
      method: 'PUT',
      headers: new Headers({
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${getJwt()}`,
      }),
      mode: 'cors',
      body: JSON.stringify(body),
      keepalive: options?.keepalive ?? false,
    });

    if (!response.ok) {
      return { error: await errorFrom(response) };
    }

    return { result: await response.json() };
  } catch (error) {
    return { error: error?.toString() || 'Something went wrong' };
  }
}

export async function _delete(url: string): Promise<HttpResponse<void>> {
  try {
    const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/${url}`, {
      method: 'DELETE',
      headers: new Headers({
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${getJwt()}`,
      }),
      mode: 'cors',
    });

    if (!response.ok) {
      return { error: response.statusText };
    }

    return {};
  } catch (error) {
    return { error: error?.toString() || 'Something went wrong' };
  }
}